import binning from "../../../data/config/binning.json";

type MeasureId = keyof typeof binning;

interface BinConfig {
  readonly edges: readonly number[];
  readonly unit: string;
  readonly format: "currency" | "percent";
}

function assertMeasure(id: string): asserts id is MeasureId {
  if (!(id in binning)) {
    throw new Error(`unknown measure id: ${id}`);
  }
}

function getConfig(measureId: MeasureId): BinConfig {
  return binning[measureId] as BinConfig;
}

export function getBinEdges(measureId: string): readonly number[] {
  assertMeasure(measureId);
  return getConfig(measureId).edges;
}

export function computeBinIndex(measureId: string, value: number | null | undefined): number | null {
  assertMeasure(measureId);
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const edges: readonly number[] = getConfig(measureId).edges;
  for (let i = 0; i < edges.length; i++) {
    // @ts-expect-error: TS limitation with array element access in loops
    if (value < edges[i]) return i;
  }
  return edges.length; // = 6, top bin
}

const currencyFmt = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
  maximumFractionDigits: 0,
});

export function formatValue(measureId: string, value: number | null | undefined): string {
  assertMeasure(measureId);
  if (value === null || value === undefined || !Number.isFinite(value)) return "-";
  const fmt = getConfig(measureId).format;
  if (fmt === "currency") return currencyFmt.format(value);
  return `${value.toFixed(1)}%`;
}

function formatEdge(measureId: MeasureId, value: number): string {
  const cfg = getConfig(measureId);
  if (cfg.format === "currency") {
    if (value >= 1000 && value % 1000 === 0) return `$${value / 1000}k`;
    return currencyFmt.format(value);
  }
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
}

export function formatBinLabel(measureId: string, binIndex: number): string {
  assertMeasure(measureId);
  const edges = getConfig(measureId).edges;
  if (binIndex <= 0) return `< ${formatEdge(measureId, edges[0]!)}`;
  if (binIndex >= edges.length) return `${formatEdge(measureId, edges[edges.length - 1]!)}+`;
  return `${formatEdge(measureId, edges[binIndex - 1]!)}-${formatEdge(measureId, edges[binIndex]!)}`;
}

export const KNOWN_MEASURES = Object.keys(binning) as readonly MeasureId[];
